import { Dispatch, SetStateAction, useContext, useState } from 'react';
import { Button, DialogActions, Stack, Tooltip, useTheme } from '@mui/material';
import { FixtureScorerRating } from '@/service/types';
import { API_METHOD } from '@/service/constants';
import { useScoringRate } from '@/service/hooks/useScoringRate';
import {
  calculateOverallScore,
  compareRatings,
} from '@/components/ComparisonRating/utils';
import { ScoringContext } from '@/contexts/scoring/context';
import {
  COMMON_STRING,
  DELETE_CONFIRMATION_STRING,
  SUCCESS_STRING,
} from '@/constants/dictionary';
import {
  AddScorerRatingMsg,
  RemoveScorerRatingMsg,
  SCORING_WORKER_HOST_ACTION,
  UpdateScorerRatingMsg,
} from '@/workers/scoring/types';
import { ConfirmationButtonSet } from '../common/ConfirmationButtonSet';

interface ComparisonRatingButtonsProps {
  oktaId: string | null;
  rating: FixtureScorerRating | undefined;
  newRating: FixtureScorerRating | undefined;
  setNewRating: Dispatch<SetStateAction<FixtureScorerRating | undefined>>;
}

export const ComparisonRatingButtons = ({
  oktaId,
  rating,
  newRating,
  setNewRating,
}: ComparisonRatingButtonsProps) => {
  const theme = useTheme();
  const {
    state: { fixtureId, collectionId },
    useDispatchWithResponse,
  } = useContext(ScoringContext);
  const { mutate } = useScoringRate();

  const [isOpenDeleteConfirmation, setIsOpenDeleteConfirmation] =
    useState(false);

  const hasChanges = !!(rating && newRating)
    ? compareRatings(rating, newRating)
    : false;

  const addScorerRating = useDispatchWithResponse<AddScorerRatingMsg>(
    SCORING_WORKER_HOST_ACTION.ADD_SCORER_RATING,
  );

  const updateScorerRating = useDispatchWithResponse<UpdateScorerRatingMsg>(
    SCORING_WORKER_HOST_ACTION.UPDATE_SCORER_RATING,
  );

  const removeScorerRating = useDispatchWithResponse<RemoveScorerRatingMsg>(
    SCORING_WORKER_HOST_ACTION.REMOVE_SCORER_RATING,
  );

  const submitHandler = async (
    action: API_METHOD.POST | API_METHOD.PUT | API_METHOD.DELETE,
  ) => {
    const defaultRequestData = {
      fixtureId,
      collectionId,
      requestTime: new Date().toISOString(),
      oktaId,
    };

    if (action === API_METHOD.DELETE) {
      await removeScorerRating.dispatch(defaultRequestData, {
        successMessage: SUCCESS_STRING.RATING_SUCCESSFULLY_REMOVED,
      });
      setIsOpenDeleteConfirmation(false);
    } else {
      const requestData = {
        ...defaultRequestData,
        quantityScore: newRating?.quantityScore,
        latencyScore: newRating?.latencyScore,
        comment: newRating?.comment,
      };

      if (action === API_METHOD.POST) {
        await addScorerRating.dispatch(requestData, {
          successMessage: SUCCESS_STRING.RATING_SUCCESSFULLY_RATED,
        });
      } else if (action === API_METHOD.PUT) {
        await updateScorerRating.dispatch(requestData, {
          successMessage: SUCCESS_STRING.RATING_SUCCESSFULLY_SAVED,
        });
      }
    }

    return await mutate();
  };

  return (
    <DialogActions
      sx={{ p: theme.spacing(2, 3), justifyContent: 'space-between' }}
    >
      <Tooltip
        placement='top'
        open={isOpenDeleteConfirmation}
        disableHoverListener
        componentsProps={{
          tooltip: {
            sx: {
              backgroundColor: theme.palette.background.paper,
              boxShadow: theme.shadows[1],
            },
          },
        }}
        title={
          <ConfirmationButtonSet
            onConfirm={() => submitHandler(API_METHOD.DELETE)}
            onClose={() => setIsOpenDeleteConfirmation(false)}
            confirmMessage={DELETE_CONFIRMATION_STRING.RATING}
          />
        }
      >
        <Button
          aria-hidden={!newRating}
          variant='text'
          onClick={() => setIsOpenDeleteConfirmation(true)}
          disabled={!(rating && newRating) || isOpenDeleteConfirmation}
        >
          {COMMON_STRING.REMOVE}
        </Button>
      </Tooltip>

      <Stack flexDirection='row' gap={2}>
        <Button
          variant='contained'
          disabled={hasChanges}
          onClick={() =>
            !!(!rating && newRating)
              ? submitHandler(API_METHOD.POST)
              : !!(rating && newRating)
              ? submitHandler(API_METHOD.PUT)
              : null
          }
        >
          {!!rating ? COMMON_STRING.SAVE : COMMON_STRING.ADD}
        </Button>
        <Button
          variant='outlined'
          disabled={hasChanges}
          onClick={() =>
            !!rating
              ? setNewRating(calculateOverallScore(rating))
              : setNewRating(undefined)
          }
        >
          {COMMON_STRING.CANCEL}
        </Button>
      </Stack>
    </DialogActions>
  );
};
